import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
} from '@angular/core';
import { Component as BrComponent, Page } from '@bloomreach/spa-sdk';
import { ResponsiveService, ToggleButton } from '@frk/eds-components';
import { SalesChargeBreakPoint, Yields } from '@models';
import { BaseProductDetailComponent } from '@products/base-product-detail.component';
import { DistributionHistory } from '@products/models/distribution-history';
import { EMDASH } from '@products/utils/constants/product.constants';
import { hasValue } from '@products/utils/mappers/mapper.utils';
import { SiteConfigService } from '@services';
import { TranslateService } from '@shared/translate/translate.service';
import {
  ConfigurationId,
  DistributionLatestConfig,
  DistributionSchedules,
  DistributionState,
  DistributionTableConfig,
  ProductDetailConfiguration,
} from '@types';
import { Logger } from '@utils/logger';
import { generateUUID } from '@utils/text/string-utils';
import cloneDeep from 'lodash/cloneDeep';
import { Observable, Subscription } from 'rxjs';
import { PricingDistributionDisplayComponent } from '../pricing-distribution-component.interface';
import { PricingDistributionConfig } from '../pricing-distribution.config';
import { DistributionChartService } from '../services/distribution-chart.service';

const logger = Logger.getLogger('DistributionChartComponent');
interface YearOption {
  label: string;
  value: string;
}
@Component({
  selector: 'ft-distribution-chart',
  templateUrl: './distribution-chart.component.html',
  styleUrls: ['./distribution-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DistributionChartComponent
  extends BaseProductDetailComponent
  implements PricingDistributionDisplayComponent, OnDestroy {
  public componentConfiguration: PricingDistributionConfig;
  private dataSubscription: Subscription[] = [];
  public salesChargeBreakPoints: SalesChargeBreakPoint[];
  public latestConfig: DistributionLatestConfig;
  public tableConfig: DistributionTableConfig;
  public filteredHistoricalList: DistributionHistory[];
  public historicalTableTotalPages: {
    count: number; // Intentionally done, as onChanges in child didn't detected number change, when same value, even on using cloneDeep().
  };
  public isSiteIntl = true;
  public schedules: DistributionSchedules;
  public showRatesAndYieldsSection = false;
  public yields: Yields;
  public productType: ConfigurationId;
  public toggleButtons: ToggleButton[] = [];
  public yearOptionList: YearOption[] = [];
  public yearOptionSelected: string;
  public id = `pricingChartDropDown${generateUUID()}`;
  public selectedTab = '';
  public highChartOptions: Highcharts.Options;
  public isHandheld$: Observable<boolean>;
  public hideDistributionPerShare = false;
  public heading: string;
  constructor(
    private distributionService: DistributionChartService,
    private changeDetector: ChangeDetectorRef,
    private translateService: TranslateService,
    protected responsiveService: ResponsiveService,
    private siteConfig: SiteConfigService
  ) {
    super();
    this.isHandheld$ = this.responsiveService.isHandheld$();
  }

  public populate(
    componentConfig: PricingDistributionConfig,
    brConfig: ProductDetailConfiguration,
    component: BrComponent,
    page: Page
  ): void {
    this.heading =
      brConfig.componentType === 'Distribution Chart'
        ? this.translateService.instant(
            'products.distribution-distributions-per-share',
            brConfig.fundId
          )
        : this.translateService.instant(
            'products.dist-distributions-per-share-and-nav',
            brConfig.fundId
          );
    this.initialize(brConfig, component, page);
    logger.debug(page, this.page);
    this.componentConfiguration = componentConfig;
    this.changeDetector.detectChanges();
    this.toggleButtons = [
      {
        active: true,
        text: this.translateService.instant('products.distribution-chart-tab'),
        value: 'chart',
      },
      {
        active: false,
        text: this.translateService.instant('products.distribution-table-tab'),
        value: 'table',
      },
    ];
    if (this.brConfig.fundId) {
      this.distributionService.setConfigurationName(
        componentConfig.configurationName
      );
      this.dataSubscription.push(
        this.distributionService.distributionState.subscribe(
          (newState: DistributionState) => this.mapState(newState)
        )
      );
      this.dataSubscription.push(
        this.distributionService
          .getCurrentPage$()
          .subscribe((historicalList) => {
            this.filteredHistoricalList = cloneDeep(historicalList);
            this.changeDetector.detectChanges();
          })
      );
      this.dataSubscription.push(
        this.distributionService.getNumberOfPages$().subscribe((totalPages) => {
          this.historicalTableTotalPages = cloneDeep({
            count: totalPages,
          });
        })
      );
      this.distributionService.populate(brConfig);
      this.hideDistributionPerShare = this.siteConfig.hideDistributionPerShare(
        this.brConfig.fundId
      );
    }

    this.selectedTab = this.toggleButtons.find(
      (toggleButton: ToggleButton) => toggleButton.active
    )?.value;
  }

  /**
   * Called when state changes.
   * @param distributionState the new state returned by distribution service
   */
  private mapState(distributionState: DistributionState): void {
    this.productType = distributionState.productType as ConfigurationId;
    this.salesChargeBreakPoints = distributionState.salesChargeBreakPoints;
    this.latestConfig = distributionState.latestConfig;
    this.tableConfig = cloneDeep(distributionState.tableConfig);
    this.highChartOptions = distributionState.highChartOptions;
    this.isSiteIntl = distributionState.isSiteIntl;
    this.schedules = distributionState.schedules;
    this.yields = distributionState.yields;

    if (distributionState?.selectedTab) {
      this.toggleButtons.forEach((toggleButton: ToggleButton) => {
        toggleButton.active =
          distributionState?.selectedTab === toggleButton.value;
        this.selectedTab = toggleButton.active
          ? toggleButton.value
          : this.selectedTab;
      });
    }
    if (
      distributionState.schedules &&
      distributionState.schedules?.dividendFrequency !== '-' &&
      distributionState.schedules.dividendFrequency !== EMDASH &&
      !distributionState.schedules.dividendFrequency
        ?.toLowerCase()
        .includes('annual') &&
      (this.salesChargeBreakPoints?.length ||
        Object.keys(this.yields).some((prop: keyof Yields) =>
          hasValue(this.yields, prop)
        ))
    ) {
      this.showRatesAndYieldsSection = true;
    }

    if (!this.yearOptionList.length) {
      this.tableConfig.dividendYears.map((year) => {
        this.yearOptionList.push({
          label:
            year === 'SINCE_INCEPTION'
              ? this.translateService.instant(
                  'products.distribution-SINCE_INCEPTION'
                )
              : year,
          value: year,
        });
      });
    }
    this.yearOptionSelected = this.tableConfig.selectedYear;
    this.changeDetector.detectChanges();
  }

  /**
   * Call distribution service to change distribution year for table.
   * This will then update state & distribution table, when service provides update.
   */
  public updateDistributionHistoryTable(selectedYear: YearOption): void {
    this.distributionService.createDistributionTable(selectedYear.value);
  }

  /**
   * Trigger page change with the page number selected.
   * @param pageNumber page number selected
   */
  public onPageChanged(pageNumber: number): void {
    this.distributionService.goToPage(pageNumber);
  }

  /**
   * Trigger download action.
   */
  public onDownloadButtonClicked(): void {
    this.distributionService.triggerDownloadAction();
  }

  /**
   * Return the title text for the option selected.
   * @param yearOptionSelected year option selected, coming from state
   */
  public getTitleText(yearOptionSelected: string): string {
    return this.yearOptionList.find(
      (yearoption) => yearoption.value === yearOptionSelected
    )?.label;
  }

  public onToggleActive(selectedOption: ToggleButton) {
    this.distributionService.updateToggle(selectedOption.value);
  }

  public ngOnDestroy(): void {
    // clean up subscription to avoid memory leaks
    this.dataSubscription?.forEach((subscription) =>
      subscription.unsubscribe()
    );
  }
}
